/**
 * دو تصمیمِ ردیفِ «جواب‌ها» در صفحه‌ی سیگنال — خالص و آزمودنی (WHOSUP-236).
 *
 * ═══ باگی که این ماژول بستش ═══
 * جمله‌ی «از بیرون دعوت شده — نمی‌تواند توی اپ جواب بدهد» به‌عنوانِ برچسبِ
 * **چیپ** رندر می‌شد. چیپ در ری‌اکت‌نیتیو کوچک نمی‌شود (پیش‌فرضِ `flexShrink`
 * آن‌جا صفر است، برخلافِ وب)، پس یک جمله تمامِ عرضِ ردیف را برمی‌داشت و ستونِ
 * اسم را تا صفر له می‌کرد: اسم حرف‌به‌حرف عمودی می‌شکست و کارتِ آن دعوت‌شده
 * چند صد پیکسل خالی می‌ماند. و چون هر دعوت‌شده‌ی «از بیرون» همین وضع را دارد،
 * از اولینشان به بعد همه خراب دیده می‌شدند.
 *
 * درمان دو تکه دارد و هر دو این‌جاست تا دوباره از هم جدا نیفتند:
 *   • چیپ فقط برچسبِ کوتاهِ جواب را می‌گیرد (`responseChipLabel`)؛
 *   • «از بیرون» یادداشتی کنارِ اسم است، مثلِ «ایمیل رفت» و «باز شد».
 *
 * هر دو اپ از همین‌جا می‌خوانند، وگرنه دفعه‌ی بعد یکی‌شان تصمیم را عوض می‌کند
 * و آن یکی همان‌طور می‌ماند.
 */
import { t } from './i18n.ts';
import type { Recipient } from '../domain/types.ts';

/**
 * دعوت‌شده‌ای که لینکِ عمومیِ پیامکِ گروهی را گرفته و هنوز به هیچ حسابی وصل
 * نشده — یعنی اصلاً راهی ندارد که توی اپ جواب بدهد.
 *
 * «از بیرون» تا وقتی‌ست که به حسابی وصل نشده — بعد از تأییدِ شماره
 * (مایگریشن 0130) دعوتِ واقعی می‌شود و عادی شمرده می‌شود.
 */
export function isExternalInvite(
  r: Pick<Recipient, 'via' | 'response' | 'recipientId'>,
): boolean {
  return r.via === 'sms_group' && r.response === 'pending' && !r.recipientId;
}

/**
 * برچسبِ چیپِ وضعیت. **همیشه کوتاه** — یک واژه یا یک عبارتِ چندکلمه‌ای،
 * هرگز یک جمله. هر چیزِ بلندتر یادداشتِ کنارِ اسم است، نه چیپ.
 *
 * تنها ورودیِ بلندشدنی برچسبِ شرطِ دست‌نویسِ خودِ کاربر است
 * (`RespondSheet`/`RespondMenu`)، که کوتاهش نمی‌کنیم چون متنِ اوست؛
 * به‌جایش هر دو اپ عرضِ چیپ را در ردیف محدود می‌کنند.
 */
export function responseChipLabel(
  r: Pick<Recipient, 'response' | 'declineReason' | 'condition'>,
): string {
  if (r.response === 'in') return t('respIn');
  if (r.response === 'out') {
    return r.declineReason === 'not_my_thing' ? t('respNotMyThing') : t('respOut');
  }
  if (r.response === 'maybe') return t('maybe');
  if (r.response === 'conditional') return r.condition?.label ?? t('conditionalShort');
  return '—';
}
